import React, { useMemo } from "react";
import { TransactionRuleCondition } from "@common/types/transactionRule";
import { useEntities } from "@hooks/useEntities";
import { RuleTagRow } from "../RuleTagRow";
import { getDirectionLabel } from "../ruleTagUtils";

interface Props {
    condition: TransactionRuleCondition;
}

export const RuleEntityCondition: React.FC<Props> = ({ condition }) => {
    const entities = useEntities();
    const label = getDirectionLabel("Entity", condition.transferDirection);
    const ids = (
        Array.isArray(condition.value) ? condition.value : [condition.value]
    ) as number[];
    const relevantEntities = entities.filter((e) => ids.includes(e.id));

    const allBusinessEntitiesSelected = useMemo(() => {
        const allBusinessIds = entities
            .filter((e) => e.isBusiness)
            .map((e) => e.id);

        return (
            allBusinessIds.every((id) =>
                relevantEntities.some((e) => e.id === id),
            ) && relevantEntities.length === allBusinessIds.length
        );
    }, [entities, relevantEntities]);

    const allPersonalEntitiesSelected = useMemo(() => {
        const allPersonalIds = entities
            .filter((e) => e.isPersonal)
            .map((e) => e.id);

        return (
            allPersonalIds.every((id) =>
                relevantEntities.some((e) => e.id === id),
            ) && relevantEntities.length === allPersonalIds.length
        );
    }, [entities, relevantEntities]);

    if (relevantEntities.length === 1) {
        return (
            <RuleTagRow
                label={label}
                type="condition"
                values={[relevantEntities[0].name ?? "(unnamed)"]}
            />
        );
    }

    if (allBusinessEntitiesSelected) {
        return (
            <RuleTagRow
                label={label}
                type="condition"
                values={["Any business entity"]}
            />
        );
    }

    if (allPersonalEntitiesSelected) {
        return (
            <RuleTagRow
                label={label}
                type="condition"
                values={["Any personal entity"]}
            />
        );
    }

    if (relevantEntities.length > 1) {
        return (
            <RuleTagRow
                label={label}
                type="condition"
                values={relevantEntities.map((e) => e.name ?? "(unnamed)")}
            />
        );
    }

    return null;
};
